import toast from 'react-hot-toast';

import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';

import { ENV } from '@/shared/config/env';
import { logger } from '@/shared/lib/logger';
import { sentryHelpers } from '@/shared/lib/sentry';
import { supabaseAuthService } from '@/shared/services/supabase-auth-service';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: ENV.API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      async (config) => {
        // Add app source header
        config.headers['X-App-Source'] = ENV.APP_PROJECT;

        // Check if we should skip token attachment (used during auth initialization)
        const skipInterceptor = (config as { _skipInterceptor?: boolean })._skipInterceptor;

        if (!skipInterceptor) {
          // Get token from Supabase session
          let token = await supabaseAuthService.getAccessToken();

          // If no token, try to refresh the session
          if (!token) {
            logger.api.debug('No token available, attempting to refresh session', {
              url: config.url,
            });
            try {
              const { session } = await supabaseAuthService.refreshSession();
              if (session) {
                token = session.access_token;
                logger.api.debug('Session refreshed successfully', { url: config.url });
              }
            } catch (refreshError) {
              logger.api.warn('Failed to refresh session in request interceptor', {
                error: refreshError,
              });
            }
          }

          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            // Log token acquisition for debugging
            logger.api.debug('API Request: Token acquired for', {
              url: config.url,
              tokenLength: token.length,
            });
          } else {
            logger.api.debug('API Request: No token available for', { url: config.url });
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & {
          _retry?: boolean;
        };

        // Handle 401 errors - simplified token refresh
        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          logger.api.debug('Got 401, attempting token refresh', { url: originalRequest.url });
          originalRequest._retry = true;

          try {
            const { session } = await supabaseAuthService.refreshSession();

            if (session) {
              logger.api.debug('Token refresh successful, retrying request');
              // Retry with new token
              if (!originalRequest.headers) {
                originalRequest.headers = {};
              }
              originalRequest.headers.Authorization = `Bearer ${session.access_token}`;
              return this.client(originalRequest);
            } else {
              logger.api.error('Token refresh returned no session');
            }
          } catch (refreshError) {
            logger.api.error('Token refresh failed', { error: refreshError });
            // Refresh failed - sign out and redirect
            await this.handleAuthFailure();
          }
        }

        // Note: Removed the check for /auth/me returning authenticated: false
        // This should be handled by the AuthProvider, not the interceptor

        // Log 400 errors (type mismatches) but don't show toast or logout
        if (error.response?.status === 400) {
          logger.api.warn('Bad Request - likely type mismatch (silently ignored)', {
            url: error.config?.url,
            method: error.config?.method,
            data: error.response?.data,
          });
          // Silently reject without showing toast
          return Promise.reject(error);
        }

        // Report non-auth errors to Sentry
        if (error.response?.status !== 401) {
          sentryHelpers.captureError(
            error instanceof Error ? error : new Error('API request failed'),
            {
              api: {
                url: error.config?.url,
                method: error.config?.method,
                status: error.response?.status,
                data: error.response?.data,
              },
            },
          );
        }

        // Show error message
        if (error.response?.data && typeof error.response.data === 'object') {
          const errorData = error.response.data as { message?: string };
          toast.error(errorData.message || 'Something went wrong');
        } else {
          toast.error('Network error');
        }

        return Promise.reject(error);
      },
    );
  }

  private async handleAuthFailure() {
    // Sign out via Supabase
    await supabaseAuthService.signOut();

    // Clear auth store
    const authStore = (await import('@/features/auth/model/auth-store')).useAuthStore.getState();
    authStore.logout();

    // Redirect to register page
    if (typeof window !== 'undefined' && window.location.pathname !== '/auth/sign-in') {
      window.location.href = '/auth/sign-in';
    }
  }

  get instance() {
    return this.client;
  }
}

export const apiClient = new ApiClient().instance;
